<template>
  <main id="app">
    <team-modal v-model="showTeamModal" @cancel="showTeamModal = false">
      <template v-slot:title>Which team are you supporting?</template>
      <p class="tdf-lede tdf-modal__lede">
        Each team is aiming to raise £1000 for Childnet this year.
      </p>
      <p class="tdf-para">
        Not sure which team to support?
        <a v-if="teams.length > 0" class="tdf-link tdf-link--white" :href="randomTeamUrl">
        Support a random team</a>
      </p>

      <div v-for="team in teams" :key="team.name">
        <h2 class="tdf-modal__subhead">{{ team.name }}</h2>
        <p class="tdf-para tdf-modal__para">
          {{ team.members.join(', ').replaceAll('.', '') }}
        </p>
        <a class="tdf-button tdf-button--orange tdf-modal__button" role="button" :href="`https://justgiving.com/page/${team.justgivingCampaignShortName}`">
          Support {{ team.name }}
        </a>
      </div>
    </team-modal>
    <canvas id="tdf-confetti-canvas"></canvas>
    <div class="tdf-top-bar">
      <div class="tdf-max-width tdf-top-bar__container">
        <h1 class="tdf-logos">
          <img src="../public/img/tdf-logo-2026.svg" alt="Tour de Fivium" class="tdf-logo">
          <a href="https://fivium.co.uk" class="tdf-fivium-logo-header">
            <img src="../public/img/fivium-logo-reverse-RGB.svg" alt="Fivium">
          </a>
        </h1>
        <nav>
          <ul class="tdf-menu">
            <li>
              <a href="#progress" class="tdf-menu__link tdf-desktop-only-block">Our progress</a>
            </li>
            <li>
              <a href="#challenge" class="tdf-menu__link tdf-desktop-only-block">The challenge</a>
            </li>
            <li>
              <a class="tdf-button tdf-button--yellow" href="https://www.justgiving.com/page/tour-de-fivium">Donate now</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div class="tdf-tracker" id="progress">
      <div class="tdf-tracker__column tdf-tracker__column--desktop" :class="{'tdf-tracker__map--hide-activity-markers': mapZoomLevel < 9}">
        <div class="tdf-tracker__stage" v-if="currentStage">
          <h2 class="tdf-tracker__stage-text">
            Stage
            <span class="tdf-tracker__stage-number">{{currentStage.properties.stageNumber}}</span>
          </h2>
          <div class="tdf-tracker__stage-name">
            {{currentStage.properties.from}} <span class="tdf-tracker__stage-to">to</span>
            {{currentStage.properties.to}}
          </div>
        </div>
        <div id="map" class="tdf-tracker__map">
        </div>
        <iframe v-if="this.wikiPageLocation.coordinates && this.wikiPageLocation.coordinates.length === 2" class="tdf-tracker__streetview" style="border:0" loading="lazy" allowfullscreen :src="`https://www.google.com/maps/embed/v1/streetview?location=${this.wikiPageLocation.coordinates[1]},${this.wikiPageLocation.coordinates[0]}&key=${gMapsApiKey}&heading=${streetViewHeading}`"></iframe>
        <input v-if="debugMode" type="number" v-model.number="debugMaxActivities">
        <span v-if="debugMode">{{streetViewHeading}}</span>
      </div>
      <div class="tdf-tracker__column tdf-tracker__column--desktop">
        <div class="tdf-tracker__row">
          <div class="tdf-tracker__column tdf-tracker__total-raised" :class="totalRaisedClass">
            <div class="tdf-stat" v-if="justgivingTotal > 0 || true">
              <span class="tdf-stat__number">£{{ Math.floor(justgivingTotal) }}</span>
              <span class="tdf-stat__label">raised for the<br>Ellen MacArthur<br>Cancer Trust</span>
            </div>

            <div>
              <a class="tdf-button tdf-button--yellow" href="https://www.justgiving.com/page/tour-de-fivium">Donate now</a>
            </div>
          </div>
          <div class="tdf-tracker__column tdf-tracker__distance">
            <div class="tdf-stat">
              <span class="tdf-stat__number">{{ (totalDistance/totalLength*100).toFixed(1).toString().replace('.0','') }}%</span>
              <span class="tdf-stat__label">complete</span>
            </div>
            <div class="tdf-tracker__row">
              <div class="tdf-tracker__column">
                <div class="tdf-stat">
                  <span class="tdf-stat__number tdf-stat__number--small">{{ totalDistance.toFixed(1).toString().replace('.0','') }}</span>
                  <span class="tdf-stat__label tdf-stat__label--small">km down</span>
                </div>
              </div>
              <div class="tdf-tracker__column">
                <div class="tdf-stat">
                  <span class="tdf-stat__number tdf-stat__number--small">{{ (totalLength - totalDistance).toFixed(1).toString().replace('.0','') }}</span>
                  <span class="tdf-stat__label tdf-stat__label--small">km to go</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="tdf-tracker__location" :style="{'background-image': (wikiPage.thumbUrl) ? `url(${wikiPage.thumbUrl})` : false}">
          <div class="tdf-tracker__location-content">
            <h2 class="tdf-tracker__location-title">
              We {{ (wikiPageLocation.isCurrent) ? 'are': 'were' }} near...
              <span class="tdf-tracker__location-name">{{ wikiPage.title }}</span>
            </h2>
            <p class="tdf-tracker__location-description">
              {{ firstSentence(wikiPage.extract) }}
            </p>
            <div>
              <a class="tdf-button" role="button" :href="`https://en.wikipedia.org/wiki/${wikiPage.title}`">
                Read more on Wikipedia
              </a>
            </div>
          </div>
        </div>

        <div class="tdf-tracker__row tdf-tracker__finish-date">
          <template v-if="lengthRequiredPerDay > 0">
            <div class="tdf-tracker__column">
              <div class="tdf-stat">
                <span class="tdf-stat__number tdf-stat__number--small">{{distanceToday.toFixed(1) }} km</span>
                <span class="tdf-stat__label tdf-stat__label--small tdf-stat__label--no-bottom-margin">today</span>
              </div>
            </div>
            <div class="tdf-tracker__column">
              <div class="tdf-stat">
                <span class="tdf-stat__number tdf-stat__number--small">{{ averageDistancePerDay.toFixed(1) }} km/day</span>
                <span class="tdf-stat__label tdf-stat__label--small tdf-stat__label--no-bottom-margin">average so far</span>
              </div>
            </div>
            <div class="tdf-tracker__column">
              <div class="tdf-stat">
                <span class="tdf-stat__number tdf-stat__number--small">{{ lengthRequiredPerDay.toFixed(1) }} km/day</span>
                <span class="tdf-stat__label tdf-stat__label--small tdf-stat__label--no-bottom-margin">needed to finish on time</span>
              </div>
            </div>
          </template>
          <div v-else-if="finalActivityIndex !== null" class="tdf-stat">
            <span class="tdf-stat__number tdf-stat__number--small">Challenge complete</span>
            <span class="tdf-stat__label tdf-stat__label--small tdf-stat__label--no-bottom-margin">
              {{ activities[finalActivityIndex].athlete.firstname }} {{ activities[finalActivityIndex].athlete.lastname }} crossed the finish line
            </span>
          </div>
        </div>

        <div class="tdf-tabs-container" v-if="false">
          <ul class="tdf-tabs">
            <li class="tdf-tabs__tab">
              <a class="tdf-tabs__link" :class="{'tdf-tabs__link--active': listToShow === 'LATEST'}" href="#" @click.prevent="switchList('LATEST')">Latest rides</a>
            </li>
            <li class="tdf-tabs__tab" v-if="false">
              <a class="tdf-tabs__link"  :class="{'tdf-tabs__link--active': listToShow === 'DAY'}" href="#" @click.prevent="switchList('DAY')">Yesterday's leaderboards</a>
            </li>
            <li class="tdf-tabs__tab" v-if="false">
              <a class="tdf-tabs__link"  :class="{'tdf-tabs__link--active': listToShow === 'TEAM'}" href="#" @click.prevent="switchList('TEAM')">Team leaderboard</a>
            </li>
            <li class="tdf-tabs__tab">
              <a class="tdf-tabs__link"  :class="{'tdf-tabs__link--active': listToShow === 'TOTAL'}" href="#" @click.prevent="switchList('TOTAL')">Leaderboard</a>
            </li>
            <li class="tdf-tabs__tab" v-if="debugMode">
              <a class="tdf-tabs__link"  :class="{'tdf-tabs__link--active': listToShow === 'COMBINED'}" href="#" @click.prevent="switchList('COMBINED')">Combined leaderboards</a>
            </li>
            <li class="tdf-tabs__tab" v-if="debugMode">
              <a class="tdf-tabs__link"  :class="{'tdf-tabs__link--active': listToShow === 'COMBINED_NORMALISED'}" href="#" @click.prevent="switchList('COMBINED_NORMALISED')">Combined normalised leaderboards</a>
            </li>
          </ul>
        </div>

        <div class="tdf-leaderboards" id="leaderboards">

          <template v-if="listToShow === 'LATEST'">
            <div class="tdf-leaderboard" v-for="leaderboard in latestActivityBoards" :key="leaderboard.displayType">
<!--              <h2 class="tdf-leaderboard__heading">-->
<!--                <img alt="" :src="`./img/${leaderboard.iconName}-marker.svg`" class="tdf-leaderboard__icon">-->
<!--                {{ leaderboard.displayType }}-->
<!--              </h2>-->
              <h2 class="tdf-leaderboard__heading">
                Latest rides
              </h2>
              <ol class="tdf-leaderboard-list" v-if="leaderboard.leaderboard.length > 0">
                <li v-for="entry in leaderboard.leaderboard" :key="entry.datetime + entry.athlete.firstname + entry.athlete.lastname" class="tdf-leaderboard-list__item tdf-leaderboard-list__item--no-number">
                  <span class="tdf-leaderboard-list__name">{{ entry.athlete.firstname }} {{ entry.athlete.lastname }}</span>
                  <span class="tdf-leaderboard-list__distance">{{ entry.distance.toFixed(1).toString().replace('.0','') }} km</span>
                </li>
              </ol>
              <p v-else class="tdf-para">
                No {{ leaderboard.displayType.toLowerCase() }} yet
              </p>
            </div>
          </template>

          <template v-if="listToShow === 'TEAM'">
            <ol class="tdf-leaderboard-list tdf-leaderboard-list--team">
              <li v-for="entry in teamLeaderboard" :key="entry.teamName" class="tdf-leaderboard-list__item">
                <span class="tdf-leaderboard-list__name" :title="entry.teamMembers.join(', ')">{{ entry.teamName }}</span>
                <span class="tdf-leaderboard-list__distance" :title="`${kmToMiles(entry.totals.run).toFixed(1).toString().replace('.0','')}mi ran, ${kmToMiles(entry.totals.ride).toFixed(1).toString().replace('.0','')}mi rode, ${kmToMiles(entry.totals.walk).toFixed(1).toString().replace('.0','')}mi walked`">{{ Math.floor(entry.totals.points) }} points</span>
              </li>
            </ol>
          </template>

<!--          <div class="tdf-leaderboard" v-for="leaderboard in leaderboardsToShow" :key="leaderboard.displayType">-->
<!--            <h2 class="tdf-leaderboard__heading">-->
<!--              <img alt="" :src="`./img/${leaderboard.iconName}-marker.svg`" class="tdf-leaderboard__icon">-->
<!--              {{ leaderboard.displayType }}-->
<!--            </h2>-->
<!--            <ol class="tdf-leaderboard-list" v-if="leaderboard.leaderboard.length > 0">-->
<!--              <li v-for="entry in leaderboard.leaderboard" :key="entry.athlete.firstname + entry.athlete.lastname" class="tdf-leaderboard-list__item">-->
<!--                <span class="tdf-leaderboard-list__name">{{ entry.athlete.firstname }} {{ entry.athlete.lastname }}</span>-->
<!--                <span class="tdf-leaderboard-list__distance">{{ kmToMiles(entry.distance).toFixed(1).toString().replace('.0','') }} miles</span>-->
<!--              </li>-->
<!--            </ol>-->
<!--            <p v-else class="tdf-para">-->
<!--              No {{ leaderboard.displayType.toLowerCase() }} yet-->
<!--            </p>-->
<!--          </div>-->
          <div class="tdf-leaderboard" v-for="leaderboard in totalLeaderboards" :key="leaderboard.displayType">
            <h2 class="tdf-leaderboard__heading">
              Leaderboard
            </h2>
            <ol class="tdf-leaderboard-list" v-if="leaderboard.leaderboard.length > 0">
              <li v-for="entry in leaderboard.leaderboard" :key="entry.athlete.firstname + entry.athlete.lastname" class="tdf-leaderboard-list__item">
                <span class="tdf-leaderboard-list__name">{{ entry.athlete.firstname }} {{ entry.athlete.lastname }}</span>
                <span class="tdf-leaderboard-list__distance">{{ entry.distance.toFixed(1).toString().replace('.0','') }} km</span>
              </li>
            </ol>
            <p v-else class="tdf-para">
              No {{ leaderboard.displayType.toLowerCase() }} yet
            </p>
          </div>

        </div>

        <div class="tdf-tracker__refresh">
          Refreshed {{ lastRefreshedText }} &bull;
          <a href="#" @click.prevent="refreshActivities">Refresh</a>
        </div>

      </div>
    </div>
    <div class="tdf-challenge tdf-max-width" id="challenge">
      <div class="tdf-half-width">
        <h2 class="tdf-paint-heading">The challenge</h2>
        <p class="tdf-lede">
          The Fivium team are supporting <a href="https://www.ellenmacarthurcancertrust.org/" class="tdf-link tdf-link--white" >the Ellen MacArthur Cancer Trust</a>
         with the return of Tour de Fivium - a virtual ride of the 2026 Tour de France route, totalling more than 3000km over three weeks.
        </p>
        <p class="tdf-para">
          From 6 - 24 July 2026 we'll be clocking up the miles on an exercise bike in our London office, and you can follow our progress in real-time as we make our way (virtually) to the Champs-Élysées.
        </p>
        <p class="tdf-para">
          Your donation will help the Ellen MacArthur Cancer Trust to support children and young people through and beyond their cancer treatment with free sailing trips and outdoor adventures, helping them to re-establish their purpose and place in the world and believe in a brighter future.
        </p>
        <p class="tdf-para">
          <a class="tdf-button tdf-button--yellow" href="https://www.justgiving.com/page/tour-de-fivium">Donate now</a>
        </p>
      </div>
    </div>
    <div class="tdf-footer tdf-max-width">
      <div>
        &copy; 2026 Fivium Ltd.
      </div>
      <a href="https://fivium.co.uk">
        <img src="../public/img/fivium-logo-reverse-RGB.svg" class="tdf-fivium-logo" alt="Fivium">
      </a>
    </div>
  </main>
</template>

<script>
  import * as turf from '@turf/turf'
  import 'whatwg-fetch'
  import ConfettiGenerator from "confetti-js";
  import TeamModal from "@/components/TeamModal.vue";

export default {
  name: 'App',
  components: {
    TeamModal
  },
  data() {
    return {
      debugMaxActivities: 999999,
      mapboxgl: require('mapbox-gl/dist/mapbox-gl'),
      stages: [],
      teams: {},
      map: null,
      mapMarkers: [],
      mapSourceIds: [],
      mapLayerIds: [],
      mapZoomLevel: 11,
      apiUrl: 'https://www.fivium.co.uk/tourdefivium-api/',
      rawActivities: [],
      currentLocationCoordinates: [],
      headingTargetCoordinates: [],
      currentStage: null,
      wikiPage: {},
      wikiPageLocation: {
        coordinates: [],
        headingTargetCoordinates: [],
        isCurrent: null
      },
      justgivingCampaignShortName: 'tour-de-fivium',
      justgivingTotal: 0,
      donationUrl: 'https://www.justgiving.com/page/tour-de-fivium',
      firstDate: '2026-07-06',
      lastDate: '2026-07-24',
      nonCountableDays: 4,
      lastRefreshDate: new Date(),
      defaultLocationBackgroundUrl: './img/default-location-bg-france.jpg',
      finalActivityIndex: null,
      now: new Date(),
      autoRefreshTimeMs: 10*60*1000, // 10 minutes
      listToShow: 'LATEST',
      newPopupOpened: false,
      confettiEnabled: false,
      gMapsApiKey: 'AIzaSyBWpDQ9nIgIlgn_GdHOC2xNuIJ74NcuHPU',
      gSheetsApiKey: 'AIzaSyBcnSnXaJiXxCR2nHYAisIP8EtXX1u19ts',
      gSheetsId: '1UuwG9R51140fX3_MfKsxO1_o97FxwQmR4VtPknGtKWY',
      gSheetName: 'Sheet1',
      showTeamModal: false
    }
  },
  computed: {
    debugMode() {
      return window.location.hash === '#debug';
    },
    totalDistance() {
      return Math.min(this.activities.reduce((acc, cur) => acc + cur.distance, 0), this.totalLength);
    },
    distanceToday() {
      return this.activities.filter((a) => a.loggedDate === this.formatDate(new Date())).reduce((acc, cur) => acc + cur.distance, 0);
    },
    totalLength() {
      return this.stages.reduce((acc, cur) => acc + cur.properties.length, 0);
    },
    totalTime() {
      return this.activities.reduce((acc, cur) => acc + cur.moving_time, 0);
    },
    lengthRequiredPerDay() {
      return (this.totalLength - this.totalDistance) / this.workDaysBetween((new Date()).setHours(0,0,0,0), this.lastDate);
    },
    averageDistancePerDay() {
      return this.totalDistance / (this.workDaysBetween(this.firstDate, (new Date()).setHours(0,0,0,0))+1);
    },
    activities() {
      if(this.finalActivityIndex === null) {
        return this.rawActivities.slice(0, this.debugMaxActivities + 1);
      } else {
        return this.rawActivities.slice(0, this.finalActivityIndex + 1);
      }
    },
    dailyLeaderboards() {
      if(this.activities) {
        return [
          { displayType: 'Runners', iconName: 'run', leaderboard: this.leaderboardForActivity('Run', 'DAY') },
          { displayType: 'Riders', iconName: 'ride',leaderboard: this.leaderboardForActivity('Ride', 'DAY') },
          { displayType: 'Walkers', iconName: 'walk',leaderboard: this.leaderboardForActivity('Walk', 'DAY') }
        ];
      } else {
        return [
          { displayType: 'Runners', iconName: 'run',leaderboard: [] },
          { displayType: 'Riders', iconName: 'ride',leaderboard: [] },
          { displayType: 'Walkers', iconName: 'walk',leaderboard: [] }
        ];
      }
    },
    totalLeaderboards() {
      if(this.activities) {
        return [
         // { displayType: 'Runners', iconName: 'run', leaderboard: this.leaderboardForActivity('Run', 'TOTAL') },
          { displayType: 'Riders', iconName: 'ride',leaderboard: this.leaderboardForActivity('Ride', 'TOTAL') },
          //{ displayType: 'Walkers', iconName: 'walk',leaderboard: this.leaderboardForActivity('Walk', 'TOTAL') }
        ];
      } else {
        return [
          //{ displayType: 'Runners', iconName: 'run',leaderboard: [] },
          { displayType: 'Riders', iconName: 'ride',leaderboard: [] },
          //{ displayType: 'Walkers', iconName: 'walk',leaderboard: [] }
        ];
      }
    },
    combinedLeaderboards() {
      if(this.activities) {
        return [
          { displayType: 'Combined', iconName: 'run', leaderboard: this.calculateCombinedLeaderboard('TOTAL') },
        ];
      } else {
        return [
          { displayType: 'Combined', iconName: 'run',leaderboard: [] },
        ];
      }
    },
    combinedNormalisedLeaderboards() {
      if(this.activities) {
        return [
          { displayType: 'Combined, normalised', iconName: 'run', leaderboard: this.calculateCombinedNormalisedLeaderboard('TOTAL') },
        ];
      } else {
        return [
          { displayType: 'Combined, normalised', iconName: 'run',leaderboard: [] },
        ];
      }
    },
    latestActivityBoards() {
      if(this.activities) {
        return [
          // { displayType: 'Runs', iconName: 'run', leaderboard: this.latestForActivity('Run') },
          { displayType: 'Rides', iconName: 'ride',leaderboard: this.latestForActivity('Ride') },
          // { displayType: 'Walks', iconName: 'walk',leaderboard: this.latestForActivity('Walk') }
        ];
      } else {
        return [
          // { displayType: 'Runs', iconName: 'run',leaderboard: [] },
          { displayType: 'Rides', iconName: 'ride',leaderboard: [] },
          // { displayType: 'Walks', iconName: 'walk',leaderboard: [] }
        ];
      }
    },
    teamLeaderboard() {
      const results = []
      this.teams.forEach((team) => {
        results.push({
          teamName: team.name,
          teamMembers: team.members,
          totals: this.activities.filter((a) => {return team.members.includes(`${a.athlete.firstname} ${a.athlete.lastname}`)}).reduce((total, activity) => {

            const miles = this.kmToMiles(activity.distance)

            let points;
            let type;

            if(['Ride','VirtualRide'].includes(activity.type)) {
              points = miles/3
              type = 'ride'
            } else if(['Walk','VirtualWalk'].includes(activity.type)) {
              points = miles * 0.75
              type = 'walk'
            } else {
              points = miles
              type = 'run'
            }

            points = points * 100;

            const result = {
              ride: total.ride + ((type === 'ride') ? activity.distance : 0),
              walk: total.walk + ((type === 'walk') ? activity.distance : 0),
              run: total.run + ((type === 'run') ? activity.distance : 0),
              points: total.points + points
            }
            return result
          }, {ride: 0, walk: 0, run: 0, points: 0})
        })
      })

      return results.sort((a, b) => b.totals.points - a.totals.points)

    },
    leaderboardsToShow() {
      if(this.listToShow === 'DAY') {
        return this.dailyLeaderboards;
      } else if(this.listToShow === 'TOTAL') {
        return this.totalLeaderboards;
      } else if (this.listToShow === 'COMBINED') {
        return this.combinedLeaderboards;
      } else if (this.listToShow === 'COMBINED_NORMALISED') {
        return this.combinedNormalisedLeaderboards;
      }else {
        return null;
      }
    },
    lastRefreshedText() {
      const mins = Math.floor((this.now - this.lastRefreshDate) / 1000 / 60);

      if(mins < 1) {
        return 'just now';
      } else if (mins === 1) {
        return '1 minute ago';
      } else {
        return `${mins} minutes ago`;
      }
    },
    totalRaisedClass() {
      if(this.activities.length > 0) {
        return `tdf-tracker__total-raised--${ this.activityTypeToIconName(this.activities[this.activities.length-1].type) }`;
      } else {
        return 'tdf-tracker__total-raised--run';
      }
    },
    streetViewHeading() {
      if(this.wikiPageLocation.coordinates.length > 0 && this.wikiPageLocation.headingTargetCoordinates.length > 0) {
        return turf.bearing(turf.point(this.wikiPageLocation.coordinates), turf.point(this.wikiPageLocation.headingTargetCoordinates));
      } else {
        return null
      }
    },
    randomTeamUrl() {
      if(this.teams.length > 0) {
        return `https://justgiving.com/page/${this.teams[this.randomIntFromInterval(0, this.teams.length-1)].justgivingCampaignShortName}`
      } else {
        return ''
      }
    }
  },
  watch: {
    currentLocationCoordinates() {
      this.wikiPageLocation.coordinates = this.currentLocationCoordinates;
      this.wikiPageLocation.headingTargetCoordinates = this.headingTargetCoordinates;
      this.wikiPageLocation.isCurrent = true;

      if(this.map) {
        this.map.panTo(this.currentLocationCoordinates, {duration: 1000})
      }
    },
    debugMaxActivities() {
      this.resetMap();
      this.setActivityMarkers();
      this.drawProgress();
    },
    wikiPageLocation: {
      deep: true,
      handler() { this.getClosestWikiPage(); }
    },
    finalActivityIndex() {
      if(this.finalActivityIndex != null && this.confettiEnabled) {
        //const burgerOrPizza = (Math.random() > 0.5) ? "burger" : "pizza";
        const confettiSettings = {
          target: 'tdf-confetti-canvas',
          props: [
            'square',
            //{ "type": "svg", "src": `./img/${burgerOrPizza}.svg`, weight: 0.1 },
          ]
        };
        const confetti = new ConfettiGenerator(confettiSettings);
        confetti.render();
      }
    }
  },
  created() {
    setInterval(() => {
      this.now = new Date();
    }, 10000) // Don't need to be precise as it's just for showing the last refresh time

    setInterval(() => {
      this.refreshActivities();
      this.getFundraisingTotal();
    }, this.autoRefreshTimeMs)

    this.getFundraisingTotal();
  },
  mounted() {
    fetch(`/data/tdf2026-6dp.json`)
        .then(response => response.json())
        .then(data => {

          this.stages = data.features;

          this.currentLocationCoordinates = this.stages[0].geometry.coordinates[0];
          this.headingTargetCoordinates = this.stages[0].geometry.coordinates[1];
          this.currentStage = this.stages[0];
          this.stages.forEach((stage) => {
            //Get the length of the stage
            const line = turf.lineString(stage.geometry.coordinates);
            stage.properties.length = turf.length(line, {units: 'kilometers'});
          });

          this.refreshActivities();
        })
        .catch(error => console.error('Error:', error));

  },
  methods: {
    randomIntFromInterval(min, max) { // min and max included
      return Math.floor(Math.random() * (max - min + 1) + min)
    },
    kmToMiles(km) {
      return km/1.60934;
    },
    switchList(list) {
      this.listToShow = list;
      this.$nextTick(() => {
        if(this.map !== null) { this.map.resize(); }
      })
    },
    refreshActivities() {
      fetch(`https://sheets.googleapis.com/v4/spreadsheets/${this.gSheetsId}/values/${this.gSheetName}?key=${this.gSheetsApiKey}`)
          .then(response => response.json())
          .then(data => {
            if(!data.values) {
              data.values = []
            }

            this.rawActivities = data.values.map(d => {
              const name = d[1].split(' ')
              return {
                datetime: d[2],
                loggedDate: d[2].substring(0,10),
                type: 'VirtualRide',
                distance: parseFloat(d[0]),
                athlete: {
                  firstname: name[0],
                  lastname: name[1]
                }
              }
            })

            this.lastRefreshDate = new Date();
            if(this.map === null) {
              this.initMap();
            } else {
              this.resetMap();
              this.setActivityMarkers();
              this.drawProgress();
            }
          })
          .catch(error => console.error('Error:', error));
      // fetch(this.apiUrl)
      //         .then(response => response.json())
      //         .then(data => {
      //           this.rawActivities = data.activities.reverse();
      //           this.lastRefreshDate = new Date();
      //           if(this.map === null) {
      //             this.initMap();
      //           } else {
      //             this.resetMap();
      //             this.setActivityMarkers();
      //             this.drawProgress();
      //           }
      //         });
    },
    getClosestWikiPage() {
      fetch(`https://en.wikipedia.org/w/api.php?action=query&generator=geosearch&prop=extracts|images&exintro=true&explaintext=true&ggscoord=${this.wikiPageLocation.coordinates[1]}%7C${this.wikiPageLocation.coordinates[0]}&ggsradius=10000&ggslimit=1&origin=*&format=json`)
              .then(response => response.json())
              .then(data => {
                this.wikiPage = { thumbUrl: null, ...data.query.pages[Object.keys(data.query.pages)[0]] };
                this.$nextTick(() => {
                  if(this.map !== null) { this.map.resize(); }
                })
                this.getWikiImageUrl();
              })
    },
    getWikiImageUrl() {
      const jpegImages = this.wikiPage.images.filter((i) => i.title.toLowerCase().endsWith('.jpg') || i.title.toLowerCase().endsWith('.jpeg'));

      if(jpegImages.length > 0) {
        fetch(`https://www.mediawiki.org/w/api.php?action=query&titles=${jpegImages[0].title}&prop=imageinfo&iiprop=url&iiurlwidth=1000&iiurlheight=1000&origin=*&format=json`)
                .then(response => response.json())
                .then(data => {
                  // eslint-disable-next-line no-prototype-builtins
                  if(data.query.pages[Object.keys(data.query.pages)[0]].hasOwnProperty('imageinfo')) {
                    this.wikiPage.thumbUrl = data.query.pages[Object.keys(data.query.pages)[0]].imageinfo[0].thumburl;
                  } else {
                    this.wikiPage.thumbUrl = this.defaultLocationBackgroundUrl;
                  }
                })
      } else {
        this.wikiPage.thumbUrl = this.defaultLocationBackgroundUrl
      }

    },
    getFundraisingTotal() {
        fetch(`https://api.justgiving.com/0f566c1d/v1/fundraising/pages/page/${this.justgivingCampaignShortName}`,
            {headers: {"Content-Type": "application/json"}}
        )
            .then(response => response.json())
            .then(data => {
              this.justgivingTotal = parseFloat(data.totalRaisedOffline) + parseFloat(data.totalRaisedOnline)
            });
      // let resultCount = 0
      // const teamCount = this.teams.length
      // let total = 0;
      //
      // this.teams.forEach((team) => {
      //   fetch(`https://api.justgiving.com/0f566c1d/v1/fundraising/pages/${team.justgivingCampaignShortName}`,
      //       {headers: {"Content-Type": "application/json"}}
      //   )
      //       .then(response => response.json())
      //       .then(data => {
      //         total += parseFloat(data.totalRaisedOffline) + parseFloat(data.totalRaisedOnline)
      //         resultCount++
      //
      //         if(resultCount === teamCount) {
      //           this.justgivingTotal = total
      //         }
      //       });
      // });

      // fetch(`https://api.justgiving.com/0f566c1d/v1/teamsv3/fivium`,
      //     {headers: {"Content-Type": "application/json"}}
      // )
      //     .then(response => response.json())
      //     .then(data => {
      //       this.justgivingTotal = data.donationSummary.totalAmount
      //     });
    },
    firstSentence(str) {
      if(str) {
        const strNoParen = str.replace(/ \(.+?\)/g, '');
        const match = /\.[ \n]/.exec(strNoParen);
        if (match) {
          return strNoParen.substring(0, match.index);
        } else {
          return strNoParen;
        }
      } else {
        return "";
      }
    },
    activityTypeToVerb(activityType) {
      switch(activityType) {
        case 'Run':
          return 'ran';
        case 'VirtualRun':
          return 'ran';
        case 'Walk':
          return 'walked';
        case 'Ride':
          return 'rode';
        case 'VirtualRide':
          return 'rode';
        default:
          return 'went';
      }
    },
    activityTypeToIconName(activityType) {
      switch(activityType) {
        case 'Run':
          return 'run';
        case 'VirtualRun':
          return 'run';
        case 'Walk':
          return 'walk';
        case 'Ride':
          return 'ride';
        case 'VirtualRide':
          return 'ride';
        default:
          return 'walk';
      }
    },
    initMap() {
      this.mapboxgl.accessToken = 'pk.eyJ1IjoiZml2aXVtIiwiYSI6ImNrZGhmdnBkMzJleHAzNHJ4aHI0cGdxZXYifQ.ebuBJnqNzmw8xYJslokxYw';

      this.map = new this.mapboxgl.Map({
        container: 'map',
        style: 'mapbox://styles/fivium/cke9pe62j0sin19pqz39yxg2h',
        center: this.currentLocationCoordinates,
        zoom: this.mapZoomLevel,
        pitch: 45,
        antialias: true
      });

      this.map.on('load', () => {
        this.map.addControl(new this.mapboxgl.NavigationControl(), 'top-left');
        this.map.addControl(new this.mapboxgl.FullscreenControl());
        this.drawStages();
        this.setActivityMarkers();
        this.drawProgress();
      });

      this.map.on('zoomend', () => {
        this.mapZoomLevel = this.map.getZoom();
      });
    },
    setActivityMarkers() {
      // Mark each activity on the map
      let cumulativeDistance = 0;
      this.finalActivityIndex = null;
      for(let i = 0; i < this.activities.length; i++) {
        const activity = this.activities[i];
        cumulativeDistance += activity.distance;

        let stageForActivity = null;
        let stagesCumulativeLength = 0;

        for(let j = 0; j < this.stages.length; j++) {
          stagesCumulativeLength += this.stages[j].properties.length;

          if(cumulativeDistance <= stagesCumulativeLength) {
            stageForActivity = this.stages[j];
            break;
          }
        }

        // There's no stage for this activity so we've finished every stage
        if(stageForActivity === null) {
          const lastStage = this.stages[this.stages.length-1];
          this.currentLocationCoordinates = lastStage.geometry.coordinates[lastStage.geometry.coordinates.length -1];
          // headingTargetCoordinates hardcoded for RTT to point into the stadium
          this.headingTargetCoordinates = [139.7167033,35.6782564];
          this.finalActivityIndex = i;
          return;
        } else {
          const stageLine = turf.lineString(stageForActivity.geometry.coordinates);
          const distanceAlongStage = turf.along(stageLine, cumulativeDistance - (stagesCumulativeLength - stageForActivity.properties.length));
          const oneMeterFurther = turf.along(stageLine, cumulativeDistance - (stagesCumulativeLength - stageForActivity.properties.length) + 0.001);

          const popup = new this.mapboxgl.Popup({offset: 25}).setHTML(
                  `<div class="tdf-map-popup"><b>${activity.athlete.firstname} ${activity.athlete.lastname}
            ${this.activityTypeToVerb(activity.type)} ${activity.distance.toFixed(1)} km</b><br>
            Total so far: ${cumulativeDistance.toFixed(1)} km</div>`
          );

          popup.on('open', () =>{
            this.newPopupOpened = true;
            this.wikiPageLocation.coordinates = distanceAlongStage.geometry.coordinates;
            this.wikiPageLocation.headingTargetCoordinates = oneMeterFurther.geometry.coordinates;
            this.wikiPageLocation.isCurrent = i === this.activities.length - 1;
            setTimeout(() => {
              this.newPopupOpened = false;
            }, 200);
          });

          popup.on('close', () => {
            setTimeout(() => {
              if(!this.newPopupOpened && !this.wikiPageLocation.isCurrent) {
                this.wikiPageLocation.coordinates = this.currentLocationCoordinates;
                this.wikiPageLocation.headingTargetCoordinates = this.headingTargetCoordinates;
                this.wikiPageLocation.isCurrent = true;
              }
            }, 100);
          });

          // Create a DOM element for the marker
          let markerEl = document.createElement('div');
          markerEl.className = 'tdf-activity-marker';
          markerEl.style.backgroundImage = `url(./img/${this.activityTypeToIconName(activity.type)}-marker.svg)`;
          if (i === this.activities.length - 1) {
            // Bigger marker for latest activity
            markerEl.style.width = '30px';
            markerEl.style.height = '30px';
          } else {
            markerEl.style.width = '20px';
            markerEl.style.height = '20px';
          }

          const marker = new this.mapboxgl.Marker(markerEl)
                  .setLngLat(distanceAlongStage.geometry.coordinates)
                  .setPopup(popup)
                  .addTo(this.map);
          this.mapMarkers.push(marker);

          if (i === this.activities.length - 1) {
            this.currentLocationCoordinates = distanceAlongStage.geometry.coordinates;
            this.headingTargetCoordinates = oneMeterFurther.geometry.coordinates;
          }
        }
      }
    },
    drawProgress() {
      let cumulativeStageLength = 0;
      let completedStagesLength = 0;
      let foundSplitStage = false;
      let idCounter = 0;
      this.stages.forEach((stage) => {
        cumulativeStageLength += stage.properties.length;

        if(cumulativeStageLength < this.totalDistance) {
          // Completed stage
          this.drawLine(stage.geometry.coordinates, '#005bbb', idCounter++)
          completedStagesLength += stage.properties.length;
          stage.properties.completed = true;
        } else if(!foundSplitStage) {
          const line = turf.lineString(stage.geometry.coordinates);
          const completedPartLength = this.totalDistance - completedStagesLength;
          // Partially completed stage
          if(completedPartLength > 0) {
            const completedLine = turf.lineSliceAlong(line, 0, completedPartLength);
            this.drawLine(completedLine.geometry.coordinates, '#005bbb', idCounter++)
          }

          foundSplitStage = true;
          this.currentStage = stage;
        }
      })
    },
    drawStages() {
      this.map.addSource(`stages-source`, {
        'type': 'geojson',
        'data': {
          "type": "FeatureCollection",
          "features": this.stages
        }
      });

      this.map.addLayer({
        'id': `stages-layer`,
        'type': 'line',
        'source': `stages-source`,
        'layout': {
          'line-join': 'round',
          'line-cap': 'round'
        },
        'paint': {
          'line-color': '#ffffff',
          'line-width': 10
        }
      }, 'tunnel-oneway-arrow-blue');

      // Draw start/end markers
      this.stages.forEach((stage, index)=> {
        // Start
        const startPopup = new this.mapboxgl.Popup({offset: 25}).setHTML(
                `<div class="tdf-map-popup"><b>Start of stage ${stage.properties.stageNumber}</b><br> ${stage.properties.from} &mdash; ${stage.properties.to}<br> ${ stage.properties.length.toFixed(1) } km</div>`
        );

        // Create a DOM element for the marker
        let startMarkerEl = document.createElement('div');
        startMarkerEl.className = `tdf-route-marker ${stage.properties.completed ? 'tdf-route-marker--completed' : ''}`;
        startMarkerEl.innerText = stage.properties.stageNumber

        const startLngLat = stage.geometry.type === 'MultiLineString' ? stage.geometry.coordinates[0][0] : stage.geometry.coordinates[0]
        new this.mapboxgl.Marker(startMarkerEl)
                .setLngLat(startLngLat)
                .setPopup(startPopup)
                .addTo(this.map);

        // End of challenge
        if(index === this.stages.length - 1) {
          // End
          const endPopup = new this.mapboxgl.Popup({offset: 25}).setHTML(
              `<div class="tdf-map-popup"><b>End of stage ${stage.properties.stageNumber}</b><br> ${stage.properties.from} &mdash; ${stage.properties.to}</div>`
          );

          // Create a DOM element for the marker
          let endMarkerEl = document.createElement('div');
          endMarkerEl.className = 'tdf-end-marker';
          endMarkerEl.style.backgroundImage = `url(./img/chequered-flag.svg)`;
          endMarkerEl.style.width = '30px';
          endMarkerEl.style.height = '30px';

          const endLngLat = stage.geometry.type === 'MultiLineString' ? stage.geometry.coordinates[stage.geometry.coordinates.length - 1][stage.geometry.coordinates[stage.geometry.coordinates.length - 1].length - 1] : stage.geometry.coordinates[stage.geometry.coordinates.length - 1]

          new this.mapboxgl.Marker(endMarkerEl)
              .setLngLat(endLngLat)
              .setPopup(endPopup)
              .addTo(this.map);
        }
      })
    },
    drawLine(coordinates, colour, id) {
      this.map.addSource(`${id}-source`, {
        'type': 'geojson',
        'data': {
          'type': 'Feature',
          'properties': {},
          'geometry': {
            'type': 'LineString',
            'coordinates': coordinates
          }
        }
      });

      this.mapSourceIds.push(`${id}-source`);

      this.map.addLayer({
        'id': `${id}-layer`,
        'type': 'line',
        'source': `${id}-source`,
        'layout': {
          'line-join': 'round',
          'line-cap': 'round'
        },
        'paint': {
          'line-color': colour,
          'line-width': 4
        }
      }, 'admin-1-boundary-bg');

      this.mapLayerIds.push(`${id}-layer`);
    },
    resetMap() {
      this.mapMarkers.forEach((m) => m.remove());
      this.mapLayerIds.forEach((s) => this.map.removeLayer(s));
      this.mapSourceIds.forEach((s) => this.map.removeSource(s));

      this.mapMarkers = [];
      this.mapLayerIds = [];
      this.mapSourceIds = [];
    },
    leaderboardForActivity(activityType, leaderboardType) {
      let athletes = {};
      let yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      this.activities.filter((a) => (a.type === activityType || a.type === `Virtual${activityType}`) && (leaderboardType === 'TOTAL' || a.loggedDate === this.formatDate(yesterday))).forEach((a) => {
        const propName = (a.athlete.firstname+a.athlete.lastname).replace('.','');
        // eslint-disable-next-line no-prototype-builtins
        if(athletes.hasOwnProperty(propName)) {
          athletes[propName].distance += a.distance;
        } else {
          athletes[propName] = {
            athlete: a.athlete,
            distance: a.distance
          }
        }
      });

      let athletesArray = Object.entries(athletes).map((o) => o[1]).sort((a, b) => b.distance - a.distance).slice(0, 5);

      return athletesArray;
    },
    calculateCombinedLeaderboard(leaderboardType) {
      let athletes = {};
      let yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      this.activities.filter((a) => ((leaderboardType === 'TOTAL' || a.loggedDate === this.formatDate(yesterday)))).forEach((a) => {
        const propName = (a.athlete.firstname+a.athlete.lastname).replace('.','');
        // eslint-disable-next-line no-prototype-builtins
        if(athletes.hasOwnProperty(propName)) {
          athletes[propName].distance += a.distance;
        } else {
          athletes[propName] = {
            athlete: a.athlete,
            distance: a.distance
          }
        }
      });

      let athletesArray = Object.entries(athletes).map((o) => o[1]).sort((a, b) => b.distance - a.distance).slice(0, 500);

      return athletesArray;
    },
    calculateCombinedNormalisedLeaderboard(leaderboardType) {
      let athletes = {};
      let yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      this.activities.filter((a) => ((leaderboardType === 'TOTAL' || a.loggedDate === this.formatDate(yesterday)))).forEach((a) => {
        const propName = (a.athlete.firstname+a.athlete.lastname).replace('.','');

        let dist;
        if(a.type === 'Ride' || a.type === 'VirtualRide') {
          dist = a.distance / 5;
        } else {
          dist = a.distance;
        }

        // eslint-disable-next-line no-prototype-builtins
        if(athletes.hasOwnProperty(propName)) {
          athletes[propName].distance += dist;
        } else {
          athletes[propName] = {
            athlete: a.athlete,
            distance: dist
          }
        }
      });

      let athletesArray = Object.entries(athletes).map((o) => o[1]).sort((a, b) => b.distance - a.distance).slice(0, 5);

      return athletesArray;
    },
    latestForActivity(activityType) {
      return this.activities.filter((a) => (a.type === activityType || a.type === `Virtual${activityType}`)).reverse().slice(0,5);
    },
    formatDate(date) {
      let d = new Date(date),
              month = '' + (d.getMonth() + 1),
              day = '' + d.getDate(),
              year = d.getFullYear();

      if (month.length < 2)
        month = '0' + month;
      if (day.length < 2)
        day = '0' + day;

      return [year, month, day].join('-');
    },
    treatAsUTC(date) {
      let result = new Date(date);
      result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
      return result;
    },
    daysBetween(startDate, endDate) {
      const millisecondsPerDay = 24 * 60 * 60 * 1000;
      return Math.ceil((this.treatAsUTC(endDate) - this.treatAsUTC(startDate)) / millisecondsPerDay);
    },
    workDaysBetween(startDate, endDate) {
      let count = 0;
      const curDate = this.treatAsUTC(startDate);
      while (curDate <= this.treatAsUTC(endDate)) {
        const dayOfWeek = curDate.getDay();
        if(dayOfWeek !== 0 && dayOfWeek !== 6) count++;
        curDate.setDate(curDate.getDate() + 1);
      }
      return count;
    }
  }
}
</script>

<style lang="scss">
@import 'assets/scss/app';
</style>
